/**
 * InspectionQuickBlock
 * --------------------
 * Kompakter Inline-Block für den Gutachter im Schritt "Kundendaten".
 * Erfasst automatisch Besichtigungstag + Ort via Browser-Geolocation
 * und Reverse-Geocoding (OpenStreetMap / Nominatim, kostenfrei).
 *
 * Props:
 *  - value: InspectionData-Teil ({ inspection_date, inspection_location, inspection_city, ... })
 *  - onChange(nextInspection): Setter, der das komplette inspection-Objekt zurückliefert
 *  - readOnly (boolean)
 */
import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin, Calendar, Loader2, Info, CheckCircle2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

// Reverse-geocode via Nominatim. No API key required, but the spec asks for a
// User-Agent-ish context and <=1 req/s. We set Accept-Language=de so addresses
// come back localised.
async function reverseGeocode(lat, lng) {
  const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&accept-language=de&zoom=18`;
  const resp = await fetch(url, { headers: { "Accept": "application/json" } });
  if (!resp.ok) throw new Error(`Nominatim HTTP ${resp.status}`);
  const data = await resp.json();
  const a = data.address || {};
  const street = [a.road, a.house_number].filter(Boolean).join(" ");
  const plz = a.postcode || "";
  const town = a.city || a.town || a.village || a.municipality || a.suburb || "";
  return {
    location: street || data.display_name?.split(",")[0] || "",
    city: [plz, town].filter(Boolean).join(" "),
    lat,
    lng,
  };
}

export default function InspectionQuickBlock({ value, onChange, readOnly = false }) {
  const insp = value || {};
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoAsked, setGeoAsked] = useState(false);

  const captureLocation = () => {
    setGeoAsked(true);
    if (!navigator.geolocation) {
      toast.error("Geolocation wird von diesem Browser nicht unterstützt.");
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const result = await reverseGeocode(latitude, longitude);
          onChange({
            ...insp,
            inspection_location: result.location,
            inspection_city: result.city,
          });
          toast.success("Standort übernommen");
        } catch (e) {
          // Fallback: at least store coordinates in the location string
          onChange({
            ...insp,
            inspection_location: `GPS: ${pos.coords.latitude.toFixed(5)}, ${pos.coords.longitude.toFixed(5)}`,
          });
          toast.warning("Adresse konnte nicht aufgelöst werden — Koordinaten gespeichert");
        } finally {
          setGeoLoading(false);
        }
      },
      (err) => {
        setGeoLoading(false);
        if (err.code === err.PERMISSION_DENIED) {
          toast.error("Standort-Zugriff verweigert. Bitte im Browser erlauben oder manuell eintragen.");
        } else if (err.code === err.POSITION_UNAVAILABLE) {
          toast.error("Kein GPS-Signal verfügbar.");
        } else if (err.code === err.TIMEOUT) {
          toast.error("Zeitüberschreitung bei der Standortabfrage.");
        } else {
          toast.error("Standort konnte nicht ermittelt werden.");
        }
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 30000 }
    );
  };

  const hasLocation = !!(insp.inspection_location || insp.inspection_city);

  return (
    <div
      className="rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50/60 to-cyan-50/40 p-4 space-y-4"
      data-testid="inspection-quick-block"
    >
      {/* Heading + consent note */}
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl brand-gradient-bg flex items-center justify-center shrink-0">
          <MapPin className="h-5 w-5 text-white" strokeWidth={1.9} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-sm tracking-tight text-zinc-900">
            Besichtigung vor Ort
          </div>
          <div className="text-xs text-zinc-600 mt-0.5">
            Tag und Ort werden automatisch erfasst — der Ort nur mit Ihrer Erlaubnis.
          </div>
        </div>
      </div>

      {/* Consent banner — shown until user has either captured or dismissed */}
      {!hasLocation && !geoAsked && (
        <div
          className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 flex items-start gap-2.5"
          data-testid="inspection-gps-hint"
        >
          <Info className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1 text-xs text-amber-900 leading-snug">
            <span className="font-semibold">Hinweis:</span>{" "}
            Wir tracken Ihren aktuellen Standort, um den Besichtigungsort automatisch
            einzutragen. Bei der Browser-Abfrage bitte <strong>„Erlauben"</strong> wählen.
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-3">
        {/* Besichtigungstag */}
        <div>
          <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500 flex items-center gap-1.5">
            <Calendar className="h-3 w-3" />
            Besichtigungstag
          </Label>
          <Input
            type="date"
            className="mt-1.5"
            value={insp.inspection_date || ""}
            onChange={(e) => onChange({ ...insp, inspection_date: e.target.value })}
            disabled={readOnly}
            data-testid="inspection-quick-date"
          />
        </div>

        {/* Besichtigungsort */}
        <div>
          <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500 flex items-center gap-1.5">
            <MapPin className="h-3 w-3" />
            Besichtigungsort
          </Label>
          <div className="mt-1.5 flex gap-2">
            <Input
              value={insp.inspection_location || ""}
              onChange={(e) => onChange({ ...insp, inspection_location: e.target.value })}
              placeholder="z. B. Autohaus Mustermann, Musterstr. 12"
              disabled={readOnly}
              data-testid="inspection-quick-location"
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={captureLocation}
              disabled={readOnly || geoLoading}
              title="Standort erfassen"
              className="shrink-0 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid="inspection-quick-gps-btn"
            >
              {geoLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
            </Button>
          </div>
          {/* PLZ/Ort erscheint zusätzlich, falls vorhanden */}
          {insp.inspection_city && (
            <div className="text-[11px] text-zinc-600 mt-1.5 flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3 text-emerald-600" />
              {insp.inspection_city}
            </div>
          )}
        </div>

        {/* Fahrbereitschaft */}
        <div>
          <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500 flex items-center gap-1.5">
            <ShieldCheck className="h-3 w-3" />
            Fahrbereitschaft
          </Label>
          <Select
            value={insp.driveable_state || "Verkehrssicher"}
            onValueChange={(v) => onChange({ ...insp, driveable_state: v })}
            disabled={readOnly}
          >
            <SelectTrigger className="mt-1.5" data-testid="inspection-quick-driveable-state">
              <SelectValue placeholder="Fahrbereitschaft wählen" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Verkehrssicher">Verkehrssicher</SelectItem>
              <SelectItem value="Bedingt verkehrssicher">Bedingt verkehrssicher</SelectItem>
              <SelectItem value="Nicht verkehrssicher">Nicht verkehrssicher</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}
